import { Gauge, Users } from 'lucide-react';

import { formatDollars } from '../../components/sec-adv-values';
import { ClientTypeName, ShareBar, Swatch } from './charts';
import type { IFirmFigures } from './firm-data';
import { Figure, NoClients, Panel } from './panel';
import { Dash, formatShare } from './register';

/**
 * A firm's Analytics (SR-423, Phase 1): what can be computed from its latest filing alone —
 * operational ratios and the client type distribution. A ratio with an input missing or nothing to
 * divide by is a dash; a "fewer than 5" client count counts as 1. History (growth, CAGR, the
 * client mix over time) waits for filing history.
 */
export function FirmAnalytics({ figures }: { figures: IFirmFigures }) {
  return (
    <div className="grid items-start gap-4 lg:grid-cols-2">
      <MetricsCard figures={figures} />
      <DistributionCard figures={figures} />
    </div>
  );
}

/** `top ÷ bottom`, or null where either is missing or `bottom` is zero — a zero `top` is a real 0. */
function ratio(top: Nullable<number>, bottom: Nullable<number>): Nullable<number> {
  if (top === null || bottom === null || bottom === 0) return null;
  return top / bottom;
}

const DECIMAL = new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 });

/** A share as "62.4": one decimal, always. */
const PERCENT = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1
});

function MetricsCard({ figures }: { figures: IFirmFigures }) {
  const { totalAum, discretionaryAum, employees, advisoryEmployees, clients, totalAccounts } =
    figures;
  const discretionary = ratio(discretionaryAum, totalAum);
  const metrics: {
    label: string;
    value: Nullable<number>;
    as: 'money' | 'decimal' | 'count' | 'percent';
  }[] = [
    { label: 'Total clients', value: clients, as: 'count' },
    {
      label: '% discretionary AUM',
      value: discretionary === null ? null : discretionary * 100,
      as: 'percent'
    },
    { label: 'AUM per employee', value: ratio(totalAum, employees), as: 'money' },
    { label: 'AUM per advisory staff', value: ratio(totalAum, advisoryEmployees), as: 'money' },
    { label: 'Clients per employee', value: ratio(clients, employees), as: 'decimal' },
    { label: 'Clients per advisory staff', value: ratio(clients, advisoryEmployees), as: 'decimal' },
    { label: 'Accounts per employee', value: ratio(totalAccounts, employees), as: 'decimal' },
    { label: 'Accounts per client', value: ratio(totalAccounts, clients), as: 'decimal' },
    { label: 'Average account size', value: ratio(totalAum, totalAccounts), as: 'money' },
    { label: 'Average client AUM', value: ratio(totalAum, clients), as: 'money' }
  ];
  return (
    <Panel
      title="Operational metrics"
      icon={Gauge}
      help={
        <>
          <p>Calculated from the firm&apos;s latest Form ADV:</p>
          <ul className="grid gap-0.5 text-xs">
            <li>Total clients — the client counts of 5.D added up</li>
            <li>% discretionary AUM — discretionary AUM (5.F(2)(a)) ÷ total AUM (5.F(2)(c))</li>
            <li>AUM per employee — total AUM ÷ employees (5.A)</li>
            <li>AUM per advisory staff — total AUM ÷ advisory employees (5.B(1))</li>
            <li>Clients per employee — total clients (5.D) ÷ employees</li>
            <li>Clients per advisory staff — total clients ÷ advisory employees</li>
            <li>Accounts per employee — total accounts (5.F(2)(f)) ÷ employees</li>
            <li>Accounts per client — total accounts ÷ total clients</li>
            <li>Average account size — total AUM ÷ total accounts</li>
            <li>Average client AUM — total AUM ÷ total clients</li>
          </ul>
          <p>
            A &quot;fewer than 5&quot; client count counts as 1. Where an input is missing, or what
            it divides by is zero, the metric is a dash.
          </p>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-4 tabular-nums">
        {metrics.map((metric) => (
          <Figure key={metric.label} label={metric.label}>
            {metric.value === null ? (
              <Dash />
            ) : metric.as === 'money' ? (
              formatDollars(Math.round(metric.value))
            ) : metric.as === 'count' ? (
              Math.round(metric.value).toLocaleString('en-US')
            ) : metric.as === 'percent' ? (
              `${PERCENT.format(metric.value)}%`
            ) : (
              DECIMAL.format(metric.value)
            )}
          </Figure>
        ))}
      </div>
    </Panel>
  );
}

function DistributionCard({ figures }: { figures: IFirmFigures }) {
  const totalClients = figures.types.reduce((sum, type) => sum + type.count, 0);
  const totalAum = figures.types.reduce((sum, type) => sum + (type.raum ?? 0), 0);
  return (
    <Panel
      title="Client type distribution"
      icon={Users}
      help={
        <p>
          Per client type (Form ADV Item 5.D): its AUM ÷ its client count, its share of all clients
          and its share of all client AUM. A &quot;fewer than 5&quot; count counts as 1.
        </p>
      }
    >
      {figures.types.length === 0 ? (
        <NoClients isEra={figures.isEra} />
      ) : (
        <ul aria-label="Client types" className="grid gap-4">
          {figures.types.map((type) => {
            const average = ratio(type.raum, type.count);
            const clientShare = totalClients > 0 ? type.count / totalClients : null;
            const aumShare = totalAum > 0 ? (type.raum ?? 0) / totalAum : null;
            return (
              <li key={type.type} className="grid gap-1.5">
                <span className="flex items-center gap-2 text-[0.6875rem] tracking-wide text-muted-foreground uppercase">
                  <Swatch color={type.color} />
                  <ClientTypeName type={type} />
                </span>
                <span className="tabular-nums">
                  {average === null ? <Dash /> : formatDollars(Math.round(average))}{' '}
                  <span className="text-muted-foreground">average AUM per client</span>
                </span>
                <span className="grid grid-cols-2 gap-4 text-xs tabular-nums">
                  <span className="grid gap-1">
                    <span>
                      {clientShare === null ? '–' : formatShare(clientShare)} of total clients
                    </span>
                    <ShareBar share={clientShare ?? 0} color={type.color} />
                  </span>
                  <span className="grid gap-1">
                    <span>{aumShare === null ? '–' : formatShare(aumShare)} of total AUM</span>
                    <ShareBar share={aumShare ?? 0} color={type.color} />
                  </span>
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </Panel>
  );
}
